/**
 * PreviewScreens - Mobile preview screen components
 *
 * Single Responsibility: Renders all mobile app screens for the preview panel
 * (podcasts list, podcast detail, episodes list, player, mini player).
 *
 * Does NOT handle: Device frame rendering, navigation logic, audio playback state.
 * Those are handled by MobilePreview and MobilePreviewFrame.
 */
package com.castly.admin.ui.preview

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.stripHtmlTags
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// Helper function to get translated text
fun getTranslatedText(
    defaultText: String,
    translations: Map<String, *>,
    language: String,
    field: String
): String {
    if (language == "en") return defaultText
    val translation = translations[language] as? Map<*, *> ?: return defaultText
    return (translation[field] as? String) ?: defaultText
}

/**
 * Loading spinner component (T014)
 * Displays a centered loading indicator
 */
@Composable
fun LoadingSpinner() {
    val colors = AppTheme.colors

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            height(100.percent)
            width(100.percent)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Color(colors.border))
                property("border-top-color", colors.primary)
                borderRadius(50.percent)
                property("animation", "spin 0.8s linear infinite")
            }
        })
        // Add CSS animation
        Style {
            "@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }"
        }
    }
}

/**
 * Error display component with retry button (T013)
 * Shows structured error information from API responses
 */
@Composable
fun ErrorDisplay(
    code: String,
    message: String,
    details: Map<String, String>? = null,
    onRetry: () -> Unit
) {
    val colors = AppTheme.colors

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            height(100.percent)
            padding(Spacing.xxl)
            textAlign("center")
        }
    }) {
        // Error icon
        Div({
            style {
                fontSize(48.px)
                color(Color(colors.danger))
                marginBottom(Spacing.lg)
            }
        }) {
            Text("!")
        }

        // Error code
        Div({
            style {
                fontSize(Typography.base)
                fontWeight("600")
                color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                marginBottom(Spacing.sm)
            }
        }) {
            Text("Error: $code")
        }

        // Error message
        Div({
            style {
                fontSize(Typography.lg)
                color(Color(colors.text))
                marginBottom(Spacing.lg)
                maxWidth(300.px)
            }
        }) {
            Text(message)
        }

        // Error details (if any)
        details?.let { detailsMap ->
            Div({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.lg)
                    textAlign("left")
                    backgroundColor(Color(colors.surfaceHover))
                    padding(Spacing.md)
                    borderRadius(BorderRadius.md)
                    maxWidth(300.px)
                }
            }) {
                detailsMap.forEach { (key, value) ->
                    Div {
                        B { Text("$key: ") }
                        Text(value)
                    }
                }
            }
        }

        // Retry button
        Button({
            onClick { onRetry() }
            style {
                padding(Spacing.md, Spacing.xxl)
                backgroundColor(Color(colors.primary))
                color(Color("#ffffff"))
                border(0.px)
                borderRadius(BorderRadius.md)
                fontSize(Typography.base)
                fontWeight("600")
                cursor("pointer")
            }
        }) {
            Text("Retry")
        }
    }
}


@Composable
fun HomeScreen(apiClient: ApiClient, selectedLanguage: String, onNavigateToContinueWatching: () -> Unit = {}, onContentClick: (com.castly.admin.dto.ContentResponse) -> Unit = {}) {
    val colors = AppTheme.colors
    var sections by remember { mutableStateOf<List<HomeSectionDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<ApiClient.ApiResult.Error?>(null) }
    var loadingContentId by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    // Fetch home page data
    fun loadHomeData() {
        scope.launch {
            loading = true
            error = null
            try {
                // Map language code: "en" -> "eng", "ar" -> "ara"
                val languageCode = if (selectedLanguage == "en") "eng" else "ara"
                when (val result = apiClient.getHomePage(languageCode)) {
                    is ApiClient.ApiResult.Success -> {
                        sections = result.data.sections
                        error = null
                    }
                    is ApiClient.ApiResult.Error -> {
                        error = result
                    }
                }
            } catch (e: Exception) {
                console.error("Failed to load home page:", e)
                error = ApiClient.ApiResult.Error("Failed to load home page", "UNKNOWN_ERROR")
            } finally {
                loading = false
            }
        }
    }

    // Load on mount and language change
    LaunchedEffect(selectedLanguage) {
        loadHomeData()
    }

    // Main container
    Div({
        style {
            height(100.percent)
            overflow("auto")
            backgroundColor(Color(colors.surfaceHover))
        }
    }) {
        when {
            loading -> LoadingSpinner()
            error != null -> ErrorDisplay(
                code = error!!.code,
                message = error!!.message,
                onRetry = { loadHomeData() }
            )
            sections.isEmpty() -> {
                // Empty state
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        height(100.percent)
                        padding(Spacing.xxl)
                        textAlign("center")
                        color(Color(colors.textMuted))
                    }
                }) {
                    Div({ style { fontSize(64.px); marginBottom(Spacing.lg) } }) {
                        Text("🏠")
                    }
                    Div({ style { fontSize(Typography.xl); fontWeight("600"); marginBottom(Spacing.sm) } }) {
                        Text("No sections yet")
                    }
                    Div({ style { fontSize(Typography.base) } }) {
                        Text("Create sections in the admin panel to see them here")
                    }
                }
            }
            else -> {
                // Display sections
                Div({
                    style {
                        padding(Spacing.lg)
                    }
                }) {
                    sections.sortedBy { it.displayOrder }.forEach { section ->
                        when (section.type) {
                            "carousel", "episode_carousel" -> {
                                // Carousel section
                                Div({
                                    style {
                                        marginBottom(Spacing.xxl)
                                    }
                                }) {
                                    // Section header
                                    section.title?.let { title ->
                                        Div({
                                            style {
                                                fontSize(Typography.xl)
                                                fontWeight("bold")
                                                color(Color(colors.text))
                                                marginBottom(Spacing.sm)
                                            }
                                        }) {
                                            Text(title)
                                        }
                                    }
                                    section.description?.let { desc ->
                                        Div({
                                            style {
                                                fontSize(Typography.base)
                                                color(Color(colors.textMuted))
                                                marginBottom(Spacing.md)
                                            }
                                        }) {
                                            Text(desc)
                                        }
                                    }

                                    // Horizontal scrolling carousel
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            property("gap", "12px")
                                            overflowX("auto")
                                            paddingBottom(Spacing.sm)
                                            property("-webkit-overflow-scrolling", "touch")
                                            property("will-change", "transform")
                                        }
                                    }) {
                                        section.items?.forEach { item ->
                                            // Carousel item card
                                            Div({
                                                style {
                                                    minWidth(140.px)
                                                    width(140.px)
                                                    flexShrink(0)
                                                    cursor("pointer")
                                                    property("opacity", if (loadingContentId == item.id) "0.6" else "1")
                                                    property("content-visibility", "auto")
                                                    property("contain-intrinsic-size", "140px 200px")
                                                }
                                                onClick {
                                                    if (loadingContentId != null) return@onClick
                                                    loadingContentId = item.id
                                                    val languageCode = if (selectedLanguage == "en") "eng" else "ara"
                                                    scope.launch {
                                                        try {
                                                            // Episode items open their parent content
                                                            when (val result = apiClient.getContentById(item.contentId ?: item.id, languageCode)) {
                                                                is ApiClient.ApiResult.Success -> onContentClick(result.data)
                                                                is ApiClient.ApiResult.Error -> console.error("Failed to load content: ${result.message}")
                                                            }
                                                        } catch (e: Exception) {
                                                            console.error("Failed to load content:", e)
                                                        } finally {
                                                            loadingContentId = null
                                                        }
                                                    }
                                                }
                                            }) {
                                                // Cover art with like indicator
                                                Div({
                                                    style {
                                                        width(140.px)
                                                        height(140.px)
                                                        backgroundColor(Color(colors.border))
                                                        borderRadius(BorderRadius.lg)
                                                        marginBottom(Spacing.sm)
                                                        overflow("hidden")
                                                        position(Position.Relative)
                                                    }
                                                }) {
                                                    if (item.coverArt != null) {
                                                        Img(item.coverArt!!) {
                                                            attr("loading", "lazy")
                                                            attr("decoding", "async")
                                                            style {
                                                                width(100.percent)
                                                                height(100.percent)
                                                                property("object-fit", "cover")
                                                            }
                                                        }
                                                    } else {
                                                        Div({
                                                            style {
                                                                width(100.percent)
                                                                height(100.percent)
                                                                display(DisplayStyle.Flex)
                                                                alignItems(AlignItems.Center)
                                                                justifyContent(JustifyContent.Center)
                                                                fontSize(40.px)
                                                                color(Color(colors.textMuted))
                                                            }
                                                        }) {
                                                            Text("♪")
                                                        }
                                                    }
                                                    // Like indicator overlay
                                                    if (item.isLiked) {
                                                        Div({
                                                            style {
                                                                position(Position.Absolute)
                                                                top(6.px)
                                                                right(6.px)
                                                            }
                                                        }) {
                                                            PreviewLikeIndicator(isLiked = true, size = 16)
                                                        }
                                                    }
                                                }
                                                // Title
                                                Div({
                                                    style {
                                                        fontSize(Typography.base)
                                                        fontWeight("600")
                                                        color(Color(colors.text))
                                                        property("overflow", "hidden")
                                                        property("text-overflow", "ellipsis")
                                                        property("white-space", "nowrap")
                                                        marginBottom(Spacing.xs)
                                                    }
                                                }) {
                                                    Text(item.title)
                                                }
                                                // Author
                                                item.author?.let { author ->
                                                    Div({
                                                        style {
                                                            fontSize(Typography.sm)
                                                            color(Color(colors.textMuted))
                                                            property("overflow", "hidden")
                                                            property("text-overflow", "ellipsis")
                                                            property("white-space", "nowrap")
                                                        }
                                                    }) {
                                                        Text(author)
                                                    }
                                                }
                                            }
                                        }

                                        // Show empty message if no items
                                        if (section.items.isNullOrEmpty()) {
                                            Div({
                                                style {
                                                    minWidth(150.px)
                                                    height(140.px)
                                                    display(DisplayStyle.Flex)
                                                    alignItems(AlignItems.Center)
                                                    justifyContent(JustifyContent.Center)
                                                    backgroundColor(Color(colors.surfaceHover))
                                                    borderRadius(BorderRadius.lg)
                                                    color(Color(colors.textMuted))
                                                    fontSize(Typography.sm)
                                                }
                                            }) {
                                                Text("No items")
                                            }
                                        }
                                    }
                                }
                            }
                            "banner" -> {
                                // Banner section
                                Div({
                                    style {
                                        width(100.percent)
                                        height(150.px)
                                        backgroundColor(Color(colors.border))
                                        borderRadius(12.px)
                                        marginBottom(Spacing.lg)
                                        overflow("hidden")
                                        property("background-image", section.imageUrl?.let { "url($it)" } ?: "none")
                                        property("background-size", "cover")
                                        property("background-position", "center")
                                        cursor(if (section.link != null) "pointer" else "default")
                                    }
                                }) {
                                    if (section.imageUrl == null) {
                                        Div({
                                            style {
                                                width(100.percent)
                                                height(100.percent)
                                                display(DisplayStyle.Flex)
                                                alignItems(AlignItems.Center)
                                                justifyContent(JustifyContent.Center)
                                                color(Color(colors.textMuted))
                                                fontSize(Typography.base)
                                            }
                                        }) {
                                            Text("Banner")
                                        }
                                    }
                                }
                            }
                            "static" -> {
                                // Static vendor section
                                Div({
                                    style {
                                        width(100.percent)
                                        height(120.px)
                                        backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#451a03"))
                                        border(2.px, LineStyle.Dashed, Color(if (colors == LightColors) "#fbbf24" else "#854d0e"))
                                        borderRadius(12.px)
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        alignItems(AlignItems.Center)
                                        justifyContent(JustifyContent.Center)
                                        marginBottom(Spacing.lg)
                                    }
                                }) {
                                    Div({ style { fontSize(32.px); marginBottom(Spacing.sm) } }) {
                                        Text("📦")
                                    }
                                    Div({ style { fontSize(Typography.base); fontWeight("600"); color(Color(if (colors == LightColors) "#92400e" else "#fcd34d")) } }) {
                                        Text("Vendor Section")
                                    }
                                    section.vendorId?.let { vendorId ->
                                        Div({ style { fontSize(Typography.sm); color(Color(if (colors == LightColors) "#b45309" else "#fbbf24")); marginTop(Spacing.xs) } }) {
                                            Text("ID: $vendorId")
                                        }
                                    }
                                }
                            }
                            "continue_watching" -> {
                                // Single hero card for most recent unfinished episode
                                section.items?.firstOrNull()?.let { item ->
                                    PreviewContinueWatchingHero(
                                        title = section.title,
                                        item = item,
                                        onResume = { carouselItem ->
                                            loadingContentId = carouselItem.id
                                            val languageCode = if (selectedLanguage == "en") "eng" else "ara"
                                            scope.launch {
                                                try {
                                                    when (val result = apiClient.getContentById(carouselItem.id, languageCode)) {
                                                        is ApiClient.ApiResult.Success -> onContentClick(result.data)
                                                        is ApiClient.ApiResult.Error -> console.error("Failed to load content: ${result.message}")
                                                    }
                                                } catch (e: Exception) {
                                                    console.error("Failed to load content:", e)
                                                } finally {
                                                    loadingContentId = null
                                                }
                                            }
                                        }
                                    )
                                }
                            }
                            "continue_watching_carousel" -> {
                                // Carousel of up to 5 unfinished episodes
                                PreviewContinueWatchingCarousel(
                                    title = section.title,
                                    description = section.description,
                                    items = section.items ?: emptyList(),
                                    onResume = { carouselItem ->
                                        loadingContentId = carouselItem.id
                                        val languageCode = if (selectedLanguage == "en") "eng" else "ara"
                                        scope.launch {
                                            try {
                                                when (val result = apiClient.getContentById(carouselItem.id, languageCode)) {
                                                    is ApiClient.ApiResult.Success -> onContentClick(result.data)
                                                    is ApiClient.ApiResult.Error -> console.error("Failed to load content: ${result.message}")
                                                }
                                            } catch (e: Exception) {
                                                console.error("Failed to load content:", e)
                                            } finally {
                                                loadingContentId = null
                                            }
                                        }
                                    },
                                    onSeeAll = { onNavigateToContinueWatching() }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun PodcastsScreen(apiClient: ApiClient, selectedLanguage: String, onSelect: (com.castly.admin.dto.ContentResponse) -> Unit) {
    val colors = AppTheme.colors
    var content by remember { mutableStateOf<List<com.castly.admin.dto.ContentResponse>>(emptyList()) }
    var allTags by remember { mutableStateOf<List<com.castly.admin.dto.TagResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var loadingMore by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<ApiClient.ApiResult.Error?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedTagIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var hasVideo by remember { mutableStateOf<Boolean?>(null) }
    var showFilters by remember { mutableStateOf(false) }
    var currentPage by remember { mutableStateOf(1) }
    var totalCount by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    val hasMore = content.size < totalCount

    // Load available tags for filtering (T037 - Phase 5 using new API)
    fun loadTags() {
        scope.launch {
            when (val result = apiClient.getTags(language = selectedLanguage)) {
                is ApiClient.ApiResult.Success -> {
                    allTags = result.data
                    error = null
                }
                is ApiClient.ApiResult.Error -> {
                    console.error("Failed to load tags:", result.message)
                    error = result
                }
            }
        }
    }

    // Load podcasts with backend filtering (T037 - Phase 5 using new API)
    fun loadContent(page: Int = 1, append: Boolean = false) {
        scope.launch {
            if (append) {
                loadingMore = true
            } else {
                loading = true
            }
            error = null

            val result = if (selectedTagIds.isNotEmpty()) {
                apiClient.searchContent(
                    query = searchQuery.takeIf { it.isNotBlank() },
                    tagIds = selectedTagIds.toList(),
                    language = selectedLanguage,
                    page = page,
                    pageSize = 20,
                    hasVideo = hasVideo
                )
            } else {
                apiClient.getContent(
                    type = "podcast",
                    language = selectedLanguage,
                    query = searchQuery.takeIf { it.isNotBlank() },
                    page = page,
                    pageSize = 20,
                    hasVideo = hasVideo
                )
            }

            when (result) {
                is ApiClient.ApiResult.Success -> {
                    if (append) {
                        content = content + result.data.items
                    } else {
                        content = result.data.items
                    }
                    totalCount = result.data.total
                    currentPage = page
                    error = null
                }
                is ApiClient.ApiResult.Error -> {
                    error = result
                }
            }
            loading = false
            loadingMore = false
        }
    }

    // Re-fetch when language changes (T026 - Phase 4)
    LaunchedEffect(selectedLanguage) {
        currentPage = 1
        loadTags()
        loadContent(page = 1, append = false)
    }

    // Debounced search - reload from backend when filters change
    LaunchedEffect(searchQuery, selectedTagIds, hasVideo) {
        kotlinx.coroutines.delay(300) // Debounce search by 300ms
        currentPage = 1
        loadContent(page = 1, append = false)
    }

    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); height(100.percent) } }) {
        // Search and filter bar
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surface))
                property("border-bottom", "1px solid ${colors.border}")
            }
        }) {
            // Search input
            Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                value(searchQuery)
                onInput { searchQuery = it.value }
                attr("placeholder", "Search podcasts...")
                style {
                    width(100.percent)
                    padding(10.px)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.lg)
                    property("box-sizing", "border-box")
                }
            }

            // Filter toggle button
            Button({
                onClick { showFilters = !showFilters }
                style {
                    width(100.percent)
                    marginTop(Spacing.sm)
                    padding(Spacing.sm)
                    backgroundColor(Color(colors.surfaceHover))
                    color(Color(colors.text))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    fontSize(13.px)
                    fontWeight("500")
                    cursor("pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(6.px)
                }
            }) {
                Text("${if (showFilters) "▼" else "▶"} Filters")
                val activeFilterCount = selectedTagIds.size
                if (activeFilterCount > 0) {
                    Span({
                        style {
                            marginLeft(Spacing.sm)
                            padding(2.px, Spacing.sm)
                            backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                            color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                            borderRadius(BorderRadius.lg)
                            fontSize(Typography.xs)
                        }
                    }) { Text("$activeFilterCount") }
                }
            }

            // Filters panel (T037 - Updated to use TagResponse with UUIDs)
            if (showFilters && allTags.isNotEmpty()) {
                Div({
                    style {
                        marginTop(Spacing.md)
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.lg)
                    }
                }) {
                    // Tags filter
                    Div {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center)
                                marginBottom(Spacing.sm)
                            }
                        }) {
                            Div({
                                style {
                                    fontSize(Typography.sm)
                                    fontWeight("600")
                                    color(Color(colors.text))
                                }
                            }) { Text("Tags") }

                            if (selectedTagIds.isNotEmpty()) {
                                Button({
                                    onClick { selectedTagIds = emptySet() }
                                    style {
                                        padding(2.px, Spacing.sm)
                                        backgroundColor(Color(colors.surface))
                                        color(Color(colors.textMuted))
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                        borderRadius(BorderRadius.sm)
                                        fontSize(10.px)
                                        cursor("pointer")
                                    }
                                }) { Text("Clear") }
                            }
                        }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                property("flex-wrap", "wrap")
                                gap(6.px)
                            }
                        }) {
                            allTags.forEach { tag ->
                                val isSelected = tag.id in selectedTagIds
                                Button({
                                    onClick {
                                        selectedTagIds = if (isSelected) {
                                            selectedTagIds - tag.id
                                        } else {
                                            selectedTagIds + tag.id
                                        }
                                    }
                                    style {
                                        padding(6.px, Spacing.md)
                                        backgroundColor(if (isSelected) Color("#7c3aed") else Color(colors.surface))
                                        color(if (isSelected) Color("#ffffff") else Color(colors.text))
                                        border(1.px, LineStyle.Solid, if (isSelected) Color("#7c3aed") else Color(colors.border))
                                        borderRadius(BorderRadius.md)
                                        fontSize(Typography.xs)
                                        cursor("pointer")
                                    }
                                }) { Text(tag.name) }
                            }
                        }
                    }

                    // Video filter (T060)
                    Div({
                        style {
                            marginTop(Spacing.md)
                            paddingTop(Spacing.md)
                            property("border-top", "1px solid ${colors.border}")
                        }
                    }) {
                        Div({
                            style {
                                fontSize(Typography.sm)
                                fontWeight("600")
                                color(Color(colors.text))
                                marginBottom(Spacing.sm)
                            }
                        }) { Text("Video") }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                gap(6.px)
                            }
                        }) {
                            // All videos option
                            Button({
                                onClick { hasVideo = null }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(if (hasVideo == null) Color("#7c3aed") else Color(colors.surface))
                                    color(if (hasVideo == null) Color("#ffffff") else Color(colors.text))
                                    border(1.px, LineStyle.Solid, if (hasVideo == null) Color("#7c3aed") else Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    fontSize(Typography.xs)
                                    cursor("pointer")
                                }
                            }) { Text("All") }

                            // Video only option
                            Button({
                                onClick { hasVideo = true }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(if (hasVideo == true) Color("#7c3aed") else Color(colors.surface))
                                    color(if (hasVideo == true) Color("#ffffff") else Color(colors.text))
                                    border(1.px, LineStyle.Solid, if (hasVideo == true) Color("#7c3aed") else Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    fontSize(Typography.xs)
                                    cursor("pointer")
                                }
                            }) { Text("Video Only") }

                            // Audio only option
                            Button({
                                onClick { hasVideo = false }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(if (hasVideo == false) Color("#7c3aed") else Color(colors.surface))
                                    color(if (hasVideo == false) Color("#ffffff") else Color(colors.text))
                                    border(1.px, LineStyle.Solid, if (hasVideo == false) Color("#7c3aed") else Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    fontSize(Typography.xs)
                                    cursor("pointer")
                                }
                            }) { Text("Audio Only") }
                        }
                    }
                }
            }
        }

        // Content list (T037 - Phase 5 using ContentResponse)
        Div({
            style {
                flex(1)
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            when {
                error != null -> {
                    ErrorDisplay(
                        code = error!!.code,
                        message = error!!.message,
                        details = error!!.details,
                        onRetry = { loadContent(page = 1, append = false) }
                    )
                }
                loading -> {
                    LoadingSpinner()
                }
                content.isEmpty() -> {
                    Div({ style { textAlign("center"); padding(40.px); color(Color(colors.textMuted)) } }) {
                        Text(if (searchQuery.isNotBlank() || selectedTagIds.isNotEmpty()) "No content found with all selected tags" else "No podcasts available")
                    }
                }
                else -> {
                    content.forEach { contentItem ->
                        ContentCard(contentItem, selectedLanguage) { onSelect(contentItem) }
                    }

                    // Load More button
                    if (hasMore) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.Center)
                                padding(Spacing.lg)
                            }
                        }) {
                            Button({
                                onClick {
                                    if (!loadingMore) {
                                        loadContent(page = currentPage + 1, append = true)
                                    }
                                }
                                style {
                                    padding(Spacing.md, Spacing.xxl)
                                    backgroundColor(if (loadingMore) Color(colors.border) else Color("#7c3aed"))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(BorderRadius.lg)
                                    fontSize(Typography.base)
                                    fontWeight("500")
                                    cursor(if (loadingMore) "not-allowed" else "pointer")
                                    property("pointer-events", if (loadingMore) "none" else "auto")
                                }
                            }) {
                                Text(if (loadingMore) "Loading..." else "Load More")
                            }
                        }
                    }

                    // Show total count
                    Div({
                        style {
                            textAlign("center")
                            padding(Spacing.sm)
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                        }
                    }) {
                        Text("Showing ${content.size} of $totalCount podcasts")
                    }
                }
            }
        }
    }
}


@Composable
fun ContentCard(content: com.castly.admin.dto.ContentResponse, selectedLanguage: String, onClick: () -> Unit) {
    val colors = AppTheme.colors
    val displayTitle = content.title
    val displayCreator = content.creators.firstOrNull()?.name ?: "Unknown Creator"
    val imageUrl = content.coverImageUrl

    Div({
        onClick { onClick() }
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(12.px)
            padding(Spacing.md)
            marginBottom(Spacing.md)
            display(DisplayStyle.Flex)
            gap(Spacing.md)
            cursor("pointer")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
            property("content-visibility", "auto")
            property("contain-intrinsic-size", "auto 76px")
        }
    }) {
        // Content artwork (with fallback)
        if (imageUrl != null) {
            Img(imageUrl) {
                attr("loading", "lazy")
                attr("decoding", "async")
                style {
                    width(60.px)
                    height(60.px)
                    borderRadius(BorderRadius.lg)
                    property("object-fit", "cover")
                    flexShrink(0)
                }
            }
        } else {
            Div({
                style {
                    width(60.px)
                    height(60.px)
                    backgroundColor(Color("#7c3aed"))
                    borderRadius(BorderRadius.lg)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(Color("#ffffff"))
                    fontSize(Typography.xxl)
                    fontWeight("bold")
                    flexShrink(0)
                }
            }) {
                Text(displayTitle.take(1).uppercase())
            }
        }
        Div({ style { flex(1); overflow("hidden") } }) {
            Div({
                style {
                    fontWeight("600")
                    fontSize(Typography.base)
                    color(Color(colors.text))
                    marginBottom(Spacing.xs)
                    property("white-space", "nowrap")
                    property("text-overflow", "ellipsis")
                    overflow("hidden")
                }
            }) { Text(displayTitle) }
            Div({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                }
            }) { Text(displayCreator) }
        }
        // Like indicator
        if (content.isLiked) {
            Div({ style { flexShrink(0); display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
                PreviewLikeIndicator(isLiked = true, size = 16)
            }
        }
    }
}

// Keep old PodcastCard for backward compatibility (will be removed in cleanup)
@Composable
fun PodcastCard(podcast: PodcastDto, selectedLanguage: String, onClick: () -> Unit) {
    val colors = AppTheme.colors
    val displayTitle = podcast.translations[selectedLanguage]?.title ?: podcast.title
    val displayAuthor = podcast.translations[selectedLanguage]?.author ?: podcast.author
    val imageUrl = podcast.coverArtUrl ?: podcast.originalCoverArtUrl

    Div({
        onClick { onClick() }
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(12.px)
            padding(Spacing.md)
            marginBottom(Spacing.md)
            display(DisplayStyle.Flex)
            gap(Spacing.md)
            cursor("pointer")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
            property("content-visibility", "auto")
            property("contain-intrinsic-size", "auto 76px")
        }
    }) {
        // Podcast artwork (with fallback)
        if (imageUrl != null) {
            Img(imageUrl) {
                attr("loading", "lazy")
                attr("decoding", "async")
                style {
                    width(60.px)
                    height(60.px)
                    borderRadius(BorderRadius.lg)
                    property("object-fit", "cover")
                    flexShrink(0)
                }
            }
        } else {
            Div({
                style {
                    width(60.px)
                    height(60.px)
                    backgroundColor(Color("#7c3aed"))
                    borderRadius(BorderRadius.lg)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(Color("#ffffff"))
                    fontSize(Typography.xxl)
                    fontWeight("bold")
                    flexShrink(0)
                }
            }) {
                Text(displayTitle.take(1).uppercase())
            }
        }
        Div({ style { flex(1); overflow("hidden") } }) {
            Div({
                style {
                    fontWeight("600")
                    fontSize(Typography.base)
                    color(Color(colors.text))
                    marginBottom(Spacing.xs)
                    property("white-space", "nowrap")
                    property("text-overflow", "ellipsis")
                    overflow("hidden")
                }
            }) { Text(displayTitle) }
            Div({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                }
            }) { Text(displayAuthor ?: "Unknown Author") }
        }
    }
}


@Composable
fun ContentDetailScreen(content: com.castly.admin.dto.ContentResponse, selectedLanguage: String, onViewEpisodes: () -> Unit, apiClient: com.castly.admin.api.ApiClient? = null) {
    val colors = AppTheme.colors
    val displayTitle = content.title
    val displayCreator = content.creators.firstOrNull()?.name ?: "Unknown Creator"
    val displayDescription = content.description
    val imageUrl = content.coverImageUrl
    var isLiked by remember { mutableStateOf(content.isLiked) }
    val scope = rememberCoroutineScope()

    Div({ style { padding(Spacing.lg) } }) {
        // Content header
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.xxl)
            }
        }) {
            // Large artwork (with fallback)
            if (imageUrl != null) {
                Img(imageUrl) {
                    style {
                        width(150.px)
                        height(150.px)
                        borderRadius(16.px)
                        property("object-fit", "cover")
                        marginBottom(Spacing.lg)
                        property("box-shadow", "0 10px 30px rgba(0, 0, 0, 0.2)")
                    }
                }
            } else {
                Div({
                    style {
                        width(150.px)
                        height(150.px)
                        backgroundColor(Color("#7c3aed"))
                        borderRadius(16.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        color(Color("#ffffff"))
                        fontSize(48.px)
                        fontWeight("bold")
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Text(displayTitle.take(1).uppercase())
                }
            }

            // Title
            Div({
                style {
                    fontSize(Typography.xl)
                    fontWeight("600")
                    color(Color(colors.text))
                    textAlign("center")
                    marginBottom(Spacing.sm)
                }
            }) { Text(displayTitle) }

            // Creator
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.md)
                }
            }) { Text(displayCreator) }

            // Content type badge
            Div({
                style {
                    display(DisplayStyle.LegacyInlineFlex)
                    padding(Spacing.xs, Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                    color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                    borderRadius(12.px)
                    fontSize(Typography.sm)
                    fontWeight("500")
                    marginBottom(Spacing.md)
                }
            }) { Text(content.type.uppercase()) }

            // Tags
            if (content.tags.isNotEmpty()) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        property("gap", "6px")
                        justifyContent(JustifyContent.Center)
                    }
                }) {
                    content.tags.take(3).forEach { tag ->
                        Div({
                            style {
                                padding(Spacing.xs, 10.px)
                                backgroundColor(Color(colors.surfaceHover))
                                color(Color(colors.text))
                                borderRadius(BorderRadius.lg)
                                fontSize(Typography.xs)
                            }
                        }) { Text(tag.name) }
                    }
                }
            }
        }

        // Like button
        if (apiClient != null) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    marginBottom(Spacing.lg)
                    marginTop(Spacing.md)
                }
            }) {
                Button({
                    onClick {
                        val previousState = isLiked
                        isLiked = !isLiked // Optimistic update
                        scope.launch {
                            val result = if (!previousState) {
                                apiClient.likeContent(content.id, content.type)
                            } else {
                                apiClient.unlikeContent(content.id)
                            }
                            if (result is com.castly.admin.api.ApiClient.ApiResult.Error) {
                                isLiked = previousState // Revert on error
                                console.error("Like/unlike failed: ${result.message}")
                            }
                        }
                    }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                        padding(Spacing.sm, Spacing.xl)
                        backgroundColor(Color(if (isLiked) "#fee2e2" else colors.surfaceHover))
                        color(Color(if (isLiked) "#dc2626" else colors.text))
                        border(1.px, LineStyle.Solid, Color(if (isLiked) "#fca5a5" else colors.border))
                        borderRadius(20.px)
                        cursor("pointer")
                        fontSize(Typography.base)
                        fontWeight("500")
                    }
                }) {
                    PreviewLikeIndicator(isLiked = isLiked, size = 18)
                    Text(if (isLiked) "Liked" else "Like")
                }
            }
        }

        // Description
        displayDescription?.let { desc ->
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.text))
                    lineHeight("1.5")
                    marginBottom(Spacing.xxl)
                }
            }) { Text(stripHtmlTags(desc)) }
        }

        // View Episodes button
        Button({
            onClick { onViewEpisodes() }
            style {
                width(100.percent)
                padding(14.px)
                backgroundColor(Color("#7c3aed"))
                color(Color("#ffffff"))
                border(0.px)
                borderRadius(12.px)
                fontSize(Typography.lg)
                fontWeight("600")
                cursor("pointer")
            }
        }) { Text("View Episodes") }
    }
}

// Keep old PodcastDetailScreen for backward compatibility (will be removed in cleanup)
@Composable
fun PodcastDetailScreen(podcast: PodcastDto, selectedLanguage: String, onViewEpisodes: () -> Unit) {
    val colors = AppTheme.colors
    val displayTitle = podcast.translations[selectedLanguage]?.title ?: podcast.title
    val displayAuthor = podcast.translations[selectedLanguage]?.author ?: podcast.author
    val displayDescription = podcast.translations[selectedLanguage]?.description ?: podcast.description
    val imageUrl = podcast.coverArtUrl ?: podcast.originalCoverArtUrl

    Div({ style { padding(Spacing.lg) } }) {
        // Podcast header
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.xxl)
            }
        }) {
            // Large artwork (with fallback)
            if (imageUrl != null) {
                Img(imageUrl) {
                    style {
                        width(150.px)
                        height(150.px)
                        borderRadius(16.px)
                        property("object-fit", "cover")
                        marginBottom(Spacing.lg)
                        property("box-shadow", "0 10px 30px rgba(0, 0, 0, 0.2)")
                    }
                }
            } else {
                Div({
                    style {
                        width(150.px)
                        height(150.px)
                        backgroundColor(Color("#7c3aed"))
                        borderRadius(16.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        color(Color("#ffffff"))
                        fontSize(48.px)
                        fontWeight("bold")
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Text(displayTitle.take(1).uppercase())
                }
            }

            // Title
            Div({
                style {
                    fontSize(Typography.xl)
                    fontWeight("600")
                    color(Color(colors.text))
                    textAlign("center")
                    marginBottom(Spacing.sm)
                }
            }) { Text(displayTitle) }

            // Author
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.md)
                }
            }) { Text(displayAuthor ?: "Unknown Author") }

            // Podcast type badge
            Div({
                style {
                    display(DisplayStyle.LegacyInlineFlex)
                    padding(Spacing.xs, Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                    color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                    borderRadius(12.px)
                    fontSize(Typography.sm)
                    fontWeight("500")
                    marginBottom(Spacing.md)
                }
            }) { Text(formatPodcastType(podcast.type)) }

            // Tags
            if (podcast.tags.isNotEmpty()) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        property("gap", "6px")
                        justifyContent(JustifyContent.Center)
                    }
                }) {
                    podcast.tags.take(3).forEach { tag ->
                        Div({
                            style {
                                padding(Spacing.xs, 10.px)
                                backgroundColor(Color(colors.surfaceHover))
                                color(Color(colors.text))
                                borderRadius(BorderRadius.lg)
                                fontSize(Typography.xs)
                            }
                        }) { Text(tag.name) }
                    }
                }
            }
        }

        // Description
        displayDescription?.let { desc ->
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.text))
                    lineHeight("1.5")
                    marginBottom(Spacing.xxl)
                }
            }) { Text(stripHtmlTags(desc)) }
        }

        // View Episodes button
        Button({
            onClick { onViewEpisodes() }
            style {
                width(100.percent)
                padding(14.px)
                backgroundColor(Color("#7c3aed"))
                color(Color("#ffffff"))
                border(0.px)
                borderRadius(12.px)
                fontSize(Typography.lg)
                fontWeight("600")
                cursor("pointer")
            }
        }) { Text("View Episodes") }
    }
}


/** Sort selector for preview episode lists. sortBy: null (default order), "releaseDate" or "title". */
@Composable
fun EpisodeSortSelector(
    sortBy: String?,
    sortDirection: String,
    onSortByChange: (String?) -> Unit,
    onDirectionChange: (String) -> Unit
) {
    val colors = AppTheme.colors
    val options = listOf<Pair<String?, String>>(null to "Default", "releaseDate" to "Release date", "title" to "Title A-Z")
    Div({
        style {
            padding(Spacing.lg)
            backgroundColor(Color(colors.surface))
            property("border-bottom", "1px solid ${colors.border}")
        }
    }) {
        Div({
            style {
                fontSize(Typography.sm)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.sm)
            }
        }) { Text("Sort") }
        Div({
            style {
                display(DisplayStyle.Flex)
                property("flex-wrap", "wrap")
                gap(6.px)
            }
        }) {
            options.forEach { (key, label) ->
                val active = sortBy == key
                Button({
                    onClick { onSortByChange(key) }
                    style {
                        padding(6.px, Spacing.md)
                        backgroundColor(if (active) Color("#7c3aed") else Color(colors.surface))
                        color(if (active) Color("#ffffff") else Color(colors.text))
                        border(1.px, LineStyle.Solid, if (active) Color("#7c3aed") else Color(colors.border))
                        borderRadius(BorderRadius.md)
                        fontSize(Typography.xs)
                        cursor("pointer")
                    }
                }) { Text(label) }
            }
            if (sortBy != null) {
                val asc = sortDirection == "ASC"
                Button({
                    onClick { onDirectionChange(if (asc) "DESC" else "ASC") }
                    style {
                        padding(6.px, Spacing.md)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        fontSize(Typography.xs)
                        cursor("pointer")
                    }
                }) { Text(if (asc) "ASC \u2191" else "DESC \u2193") }
            }
        }
    }
}


@Composable
fun EpisodesScreen(apiClient: ApiClient, content: com.castly.admin.dto.ContentResponse, selectedLanguage: String, onPlay: (com.castly.admin.dto.EpisodeResponse) -> Unit) {
    val colors = AppTheme.colors
    var allEpisodes by remember { mutableStateOf<List<com.castly.admin.dto.EpisodeResponse>>(emptyList()) }
    var episodes by remember { mutableStateOf<List<com.castly.admin.dto.EpisodeResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<ApiClient.ApiResult.Error?>(null) }
    var selectedSeason by remember { mutableStateOf<Int?>(null) }
    var sortBy by remember { mutableStateOf<String?>(null) }
    var sortDirection by remember { mutableStateOf("ASC") }
    val scope = rememberCoroutineScope()

    // Extract available seasons from episodes
    val availableSeasons = remember(allEpisodes) {
        allEpisodes
            .mapNotNull { it.seasonNumber }
            .distinct()
            .sorted()
    }

    // Load episodes when content or language changes (T041 - Phase 5)
    fun loadEpisodes() {
        scope.launch {
            loading = true
            error = null

            when (val result = apiClient.getContentEpisodes(
                contentId = content.id,
                language = selectedLanguage,
                sortBy = sortBy,
                sortDirection = sortBy?.let { sortDirection }
            )) {
                is ApiClient.ApiResult.Success -> {
                    allEpisodes = result.data
                    episodes = result.data
                    error = null
                }
                is ApiClient.ApiResult.Error -> {
                    error = result
                }
            }
            loading = false
        }
    }

    LaunchedEffect(content.id, selectedLanguage, sortBy, sortDirection) {
        loadEpisodes()
    }

    LaunchedEffect(selectedSeason) {
        episodes = if (selectedSeason == null) {
            allEpisodes
        } else {
            allEpisodes.filter { it.seasonNumber == selectedSeason }
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            height(100.percent)
        }
    }) {
        EpisodeSortSelector(sortBy, sortDirection, { sortBy = it }, { sortDirection = it })

        // Season filter (if seasons exist)
        if (availableSeasons.isNotEmpty()) {
            Div({
                style {
                    padding(Spacing.lg)
                    backgroundColor(Color(colors.surface))
                    property("border-bottom", "1px solid ${colors.border}")
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.sm)
                        fontWeight("600")
                        color(Color(colors.text))
                        marginBottom(Spacing.sm)
                    }
                }) { Text("Season") }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        gap(6.px)
                    }
                }) {
                    // "All" button
                    Button({
                        onClick { selectedSeason = null }
                        style {
                            padding(6.px, Spacing.md)
                            backgroundColor(if (selectedSeason == null) Color("#7c3aed") else Color(colors.surface))
                            color(if (selectedSeason == null) Color("#ffffff") else Color(colors.text))
                            border(1.px, LineStyle.Solid, if (selectedSeason == null) Color("#7c3aed") else Color(colors.border))
                            borderRadius(BorderRadius.md)
                            fontSize(Typography.xs)
                            cursor("pointer")
                        }
                    }) { Text("All Seasons") }

                    availableSeasons.forEach { season ->
                        Button({
                            onClick { selectedSeason = season }
                            style {
                                padding(6.px, Spacing.md)
                                backgroundColor(if (selectedSeason == season) Color("#7c3aed") else Color(colors.surface))
                                color(if (selectedSeason == season) Color("#ffffff") else Color(colors.text))
                                border(1.px, LineStyle.Solid, if (selectedSeason == season) Color("#7c3aed") else Color(colors.border))
                                borderRadius(BorderRadius.md)
                                fontSize(Typography.xs)
                                cursor("pointer")
                            }
                        }) { Text("Season $season") }
                    }
                }
            }
        }

        // Episodes list (T032 - Added error display and loading spinner)
        Div({
            style {
                flex(1)
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            when {
                error != null -> {
                    ErrorDisplay(
                        code = error!!.code,
                        message = error!!.message,
                        details = error!!.details,
                        onRetry = { loadEpisodes() }
                    )
                }
                loading -> {
                    LoadingSpinner()
                }
                episodes.isEmpty() -> {
                    Div({ style { textAlign("center"); padding(40.px); color(Color(colors.textMuted)) } }) {
                        Text(if (selectedSeason != null) "No episodes in this season" else "No episodes published yet")
                    }
                }
                else -> {
                    episodes.forEach { episode ->
                        EpisodeCard(episode, content, selectedLanguage) { onPlay(episode) }
                    }
                }
            }
        }
    }
}


@Composable
fun EpisodeCard(episode: com.castly.admin.dto.EpisodeResponse, content: com.castly.admin.dto.ContentResponse, selectedLanguage: String, onPlay: () -> Unit) {
    val colors = AppTheme.colors
    val displayTitle = episode.title
    val displayDescription = episode.description

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(12.px)
            padding(Spacing.md)
            marginBottom(Spacing.md)
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
            property("content-visibility", "auto")
            property("contain-intrinsic-size", "auto 120px")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.md)
                marginBottom(Spacing.sm)
            }
        }) {
            // Episode/Content image thumbnail - prefer episode image, fall back to content cover
            val imageUrl = episode.imageUrl ?: content.coverImageUrl
            if (imageUrl != null) {
                Img(imageUrl) {
                    attr("loading", "lazy")
                    attr("decoding", "async")
                    style {
                        width(60.px)
                        height(60.px)
                        borderRadius(BorderRadius.lg)
                        property("object-fit", "cover")
                        flexShrink(0)
                    }
                }
            } else {
                // Fallback placeholder
                Div({
                    style {
                        width(60.px)
                        height(60.px)
                        backgroundColor(Color("#7c3aed"))
                        borderRadius(BorderRadius.lg)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        color(Color("#ffffff"))
                        fontSize(Typography.xl)
                        fontWeight("bold")
                        flexShrink(0)
                    }
                }) {
                    Text(displayTitle.take(1).uppercase())
                }
            }

            Div({ style { flex(1) } }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Div({
                        style {
                            fontWeight("600")
                            fontSize(Typography.base)
                            color(Color(colors.text))
                        }
                    }) {
                        // Show season and episode numbers if available
                        if (episode.seasonNumber != null && episode.episodeNumber != null) {
                            Text("S${episode.seasonNumber}E${episode.episodeNumber}: ")
                        } else if (episode.episodeNumber != null) {
                            Text("Ep. ${episode.episodeNumber}: ")
                        }
                        Text(displayTitle)
                    }

                    // Video indicator badge (T058)
                    if (episode.video != null) {
                        Div({
                            style {
                                display(DisplayStyle.LegacyInlineFlex)
                                alignItems(AlignItems.Center)
                                padding(2.px, 6.px)
                                backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                                color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                                borderRadius(BorderRadius.sm)
                                fontSize(10.px)
                                fontWeight("600")
                                property("white-space", "nowrap")
                            }
                        }) {
                            Text("🎥 VIDEO")
                        }
                    }
                }
                Div({
                    style {
                        fontSize(Typography.sm)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text(episode.audioDurationSeconds?.let { formatDurationPreview(it) } ?: "--")
                }
            }
        }

        displayDescription?.let { desc ->
            Div({
                style {
                    fontSize(13.px)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.md)
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    overflow("hidden")
                }
            }) { Text(stripHtmlTags(desc)) }
        }

        // Play button
        Button({
            onClick { onPlay() }
            style {
                width(100.percent)
                padding(10.px)
                backgroundColor(Color("#7c3aed"))
                color(Color("#ffffff"))
                border(0.px)
                borderRadius(BorderRadius.lg)
                fontSize(Typography.base)
                fontWeight("500")
                cursor("pointer")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(Spacing.sm)
            }
        }) {
            Text("Play")
        }
    }
}


@Composable
fun PlayerScreen(
    content: com.castly.admin.dto.ContentResponse,
    episode: com.castly.admin.dto.EpisodeResponse,
    selectedLanguage: String,
    isPlaying: Boolean,
    onPlayPause: () -> Unit
) {
    val colors = AppTheme.colors
    val displayContentTitle = content.title
    val displayEpisodeTitle = episode.title
    // Image fallback chain - episode image or content cover
    val imageUrl = episode.imageUrl ?: content.coverImageUrl

    Div({
        style {
            padding(Spacing.xxl)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            height(100.percent)
            property("box-sizing", "border-box")
        }
    }) {
        // Large artwork (with full fallback chain)
        if (imageUrl != null) {
            Img(imageUrl) {
                style {
                    width(200.px)
                    height(200.px)
                    borderRadius(16.px)
                    property("object-fit", "cover")
                    marginBottom(Spacing.xxl)
                    property("box-shadow", "0 10px 40px rgba(0, 0, 0, 0.3)")
                }
            }
        } else {
            Div({
                style {
                    width(200.px)
                    height(200.px)
                    backgroundColor(Color("#7c3aed"))
                    borderRadius(16.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(Color("#ffffff"))
                    fontSize(64.px)
                    fontWeight("bold")
                    marginBottom(Spacing.xxl)
                    property("box-shadow", "0 10px 40px rgba(124, 58, 237, 0.3)")
                }
            }) {
                Text(displayContentTitle.take(1).uppercase())
            }
        }

        // Episode info
        Div({
            style {
                textAlign("center")
                marginBottom(32.px)
                width(100.percent)
            }
        }) {
            Div({
                style {
                    fontWeight("600")
                    fontSize(Typography.xl)
                    color(Color(colors.text))
                    marginBottom(Spacing.sm)
                }
            }) { Text(displayEpisodeTitle) }
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.xs)
                }
            }) { Text(displayContentTitle) }

            // Duration and Creator
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                    justifyContent(JustifyContent.Center)
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                    marginTop(Spacing.sm)
                }
            }) {
                Span { Text(episode.audioDurationSeconds?.let { formatDurationPreview(it) } ?: "--") }
                if (content.creators.isNotEmpty()) {
                    Span { Text("•") }
                }

                if (content.creators.isNotEmpty()) {
                    Span { Text(content.creators.first().name) }
                }
            }

            // Video information display (T059)
            episode.video?.let { videoObj ->
                Div({
                    style {
                        marginTop(Spacing.lg)
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.lg)
                        fontSize(Typography.sm)
                    }
                }) {
                    Div({
                        style {
                            fontWeight("600")
                            color(Color(colors.text))
                            marginBottom(Spacing.sm)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                        }
                    }) {
                        Text("🎥 Video Available")
                    }
                    Div({
                        style {
                            color(Color(colors.textMuted))
                            fontSize(Typography.xs)
                        }
                    }) {
                        Div {
                            B { Text("ID: ") }
                            Text(videoObj.id)
                        }
                        videoObj.provider?.let { provider ->
                            Div {
                                B { Text("Provider: ") }
                                Text(provider)
                            }
                        }
                        Div {
                            B { Text("URL: ") }
                            Text(videoObj.url.take(50) + if (videoObj.url.length > 50) "..." else "")
                        }
                    }
                }
            }
        }

        // Playback controls
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.xxl)
            }
        }) {
            // Rewind
            Button({
                style {
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    backgroundColor(Color(colors.surfaceHover))
                    border(0.px)
                    cursor("pointer")
                    fontSize(Typography.lg)
                }
            }) { Text("-15") }

            // Play/Pause
            Button({
                onClick { onPlayPause() }
                style {
                    width(72.px)
                    height(72.px)
                    borderRadius(50.percent)
                    backgroundColor(Color("#7c3aed"))
                    color(Color("#ffffff"))
                    border(0.px)
                    cursor("pointer")
                    fontSize(Typography.xxl)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) { Text(if (isPlaying) "||" else ">") }

            // Forward
            Button({
                style {
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    backgroundColor(Color(colors.surfaceHover))
                    border(0.px)
                    cursor("pointer")
                    fontSize(Typography.lg)
                }
            }) { Text("+30") }
        }
    }
}


@Composable
fun MiniPlayer(
    episode: EpisodeDto,
    selectedLanguage: String,
    isPlaying: Boolean,
    onPlayPause: () -> Unit,
    onClick: () -> Unit
) {
    val colors = AppTheme.colors
    val displayTitle = episode.translations[selectedLanguage]?.title ?: episode.title
    Div({
        onClick { onClick() }
        style {
            padding(Spacing.md, Spacing.lg)
            backgroundColor(Color("#7c3aed"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.md)
            cursor("pointer")
        }
    }) {
        // Mini artwork
        Div({
            style {
                width(40.px)
                height(40.px)
                backgroundColor(Color("#6d28d9"))
                borderRadius(BorderRadius.md)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                color(Color("#ffffff"))
                fontSize(Typography.lg)
                fontWeight("bold")
                flexShrink(0)
            }
        }) { Text(displayTitle.take(1).uppercase()) }

        // Title
        Div({
            style {
                flex(1)
                color(Color("#ffffff"))
                fontSize(Typography.base)
                fontWeight("500")
                property("white-space", "nowrap")
                property("text-overflow", "ellipsis")
                overflow("hidden")
            }
        }) { Text(displayTitle) }

        // Play/Pause button
        Button({
            onClick {
                it.stopPropagation()
                onPlayPause()
            }
            style {
                width(36.px)
                height(36.px)
                borderRadius(50.percent)
                backgroundColor(Color("#ffffff"))
                color(Color("#7c3aed"))
                border(0.px)
                cursor("pointer")
                fontSize(Typography.base)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexShrink(0)
            }
        }) { Text(if (isPlaying) "||" else ">") }
    }
}

// Unified MiniPlayer for both podcasts and audiobooks (T048-T049)
@Composable
fun MiniPlayerUnified(
    episode: com.castly.admin.dto.EpisodeResponse,
    content: com.castly.admin.dto.ContentResponse,
    selectedLanguage: String,
    isPlaying: Boolean,
    onPlayPause: () -> Unit,
    onClick: () -> Unit
) {
    val colors = AppTheme.colors
    val displayTitle = episode.title

    Div({
        onClick { onClick() }
        style {
            padding(Spacing.md, Spacing.lg)
            backgroundColor(Color("#7c3aed"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.md)
            cursor("pointer")
        }
    }) {
        // Mini artwork
        Div({
            style {
                width(40.px)
                height(40.px)
                backgroundColor(Color("#6d28d9"))
                borderRadius(BorderRadius.md)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                color(Color("#ffffff"))
                fontSize(Typography.lg)
                fontWeight("bold")
                flexShrink(0)
            }
        }) { Text(displayTitle.take(1).uppercase()) }

        // Title
        Div({
            style {
                flex(1)
                color(Color("#ffffff"))
                fontSize(Typography.base)
                fontWeight("500")
                property("white-space", "nowrap")
                property("text-overflow", "ellipsis")
                overflow("hidden")
            }
        }) { Text(displayTitle) }

        // Play/Pause button
        Button({
            onClick {
                it.stopPropagation()
                onPlayPause()
            }
            style {
                width(36.px)
                height(36.px)
                borderRadius(50.percent)
                backgroundColor(Color("#ffffff"))
                color(Color("#7c3aed"))
                border(0.px)
                cursor("pointer")
                fontSize(Typography.base)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexShrink(0)
            }
        }) { Text(if (isPlaying) "||" else ">") }
    }
}

private fun formatDurationPreview(seconds: Int): String {
    val hours = seconds / 3600
    val minutes = (seconds % 3600) / 60
    return if (hours > 0) "${hours}h ${minutes}m" else "${minutes} min"
}

private fun formatPodcastType(type: String): String {
    return when (type.uppercase()) {
        "PODCAST" -> "Podcast"
        "RADIO_SHOW" -> "Radio Show"
        "QURAAN" -> "Qur'an"
        "AUDIOBOOK" -> "Audiobook"
        "TV_SERIES" -> "TV Series"
        "NEWS" -> "News"
        "DOCUMENTARY" -> "Documentary"
        "COURSE" -> "Course"
        "BOOKS" -> "Books"
        else -> type
    }
}

// ==============================================================
// AUDIOBOOK SCREENS - Reusing podcast components
// ==============================================================

@Composable
fun AudiobooksScreen(apiClient: ApiClient, selectedLanguage: String, onSelect: (com.castly.admin.dto.ContentResponse) -> Unit) {
    val colors = AppTheme.colors
    var content by remember { mutableStateOf<List<com.castly.admin.dto.ContentResponse>>(emptyList()) }
    var allTags by remember { mutableStateOf<List<com.castly.admin.dto.TagResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var loadingMore by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<ApiClient.ApiResult.Error?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedTagIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var hasVideo by remember { mutableStateOf<Boolean?>(null) }
    var showFilters by remember { mutableStateOf(false) }
    var currentPage by remember { mutableStateOf(1) }
    var totalCount by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    val hasMore = content.size < totalCount

    // Load available tags for filtering (T038 - Phase 5 using new API)
    fun loadTags() {
        scope.launch {
            when (val result = apiClient.getTags(language = selectedLanguage)) {
                is ApiClient.ApiResult.Success -> {
                    allTags = result.data
                    error = null
                }
                is ApiClient.ApiResult.Error -> {
                    console.error("Failed to load tags:", result.message)
                    error = result
                }
            }
        }
    }

    // Load audiobooks with backend filtering (T038 - Phase 5 using new API)
    fun loadContent(page: Int = 1, append: Boolean = false) {
        scope.launch {
            if (append) {
                loadingMore = true
            } else {
                loading = true
            }
            error = null

            val result = if (selectedTagIds.isNotEmpty()) {
                apiClient.searchContent(
                    query = searchQuery.takeIf { it.isNotBlank() },
                    tagIds = selectedTagIds.toList(),
                    language = selectedLanguage,
                    page = page,
                    pageSize = 20,
                    hasVideo = hasVideo
                )
            } else {
                apiClient.getContent(
                    type = "audiobook",
                    language = selectedLanguage,
                    query = searchQuery.takeIf { it.isNotBlank() },
                    page = page,
                    pageSize = 20,
                    hasVideo = hasVideo
                )
            }

            when (result) {
                is ApiClient.ApiResult.Success -> {
                    if (append) {
                        content = content + result.data.items
                    } else {
                        content = result.data.items
                    }
                    totalCount = result.data.total
                    currentPage = page
                    error = null
                }
                is ApiClient.ApiResult.Error -> {
                    error = result
                }
            }
            loading = false
            loadingMore = false
        }
    }

    // Re-fetch when language changes (T027 - Phase 4)
    LaunchedEffect(selectedLanguage) {
        currentPage = 1
        loadTags()
        loadContent(page = 1, append = false)
    }

    // Debounced search - reload from backend when filters change
    LaunchedEffect(searchQuery, selectedTagIds, hasVideo) {
        kotlinx.coroutines.delay(300) // Debounce search by 300ms
        currentPage = 1
        loadContent(page = 1, append = false)
    }

    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); height(100.percent) } }) {
        // Search and filter bar
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surface))
                property("border-bottom", "1px solid ${colors.border}")
            }
        }) {
            // Search input
            Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                value(searchQuery)
                onInput { searchQuery = it.value }
                attr("placeholder", "Search audiobooks...")
                style {
                    width(100.percent)
                    padding(10.px)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.lg)
                    property("box-sizing", "border-box")
                }
            }

            // Filter toggle button
            Button({
                onClick { showFilters = !showFilters }
                style {
                    width(100.percent)
                    marginTop(Spacing.sm)
                    padding(Spacing.sm)
                    backgroundColor(Color(colors.surfaceHover))
                    color(Color(colors.text))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    fontSize(13.px)
                    fontWeight("500")
                    cursor("pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(6.px)
                }
            }) {
                Text("${if (showFilters) "▼" else "▶"} Filters")
                val activeFilterCount = selectedTagIds.size
                if (activeFilterCount > 0) {
                    Span({
                        style {
                            marginLeft(Spacing.sm)
                            padding(2.px, Spacing.sm)
                            backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                            color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                            borderRadius(BorderRadius.lg)
                            fontSize(Typography.xs)
                        }
                    }) { Text("$activeFilterCount") }
                }
            }

            // Filters panel (T038 - Updated to use TagResponse with UUIDs)
            if (showFilters && allTags.isNotEmpty()) {
                Div({
                    style {
                        marginTop(Spacing.md)
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.lg)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            marginBottom(Spacing.sm)
                        }
                    }) {
                        Div({
                            style {
                                fontSize(Typography.sm)
                                fontWeight("600")
                                color(Color(colors.text))
                            }
                        }) { Text("Tags") }

                        if (selectedTagIds.isNotEmpty()) {
                            Button({
                                onClick { selectedTagIds = emptySet() }
                                style {
                                    padding(2.px, Spacing.sm)
                                    backgroundColor(Color(colors.surface))
                                    color(Color(colors.textMuted))
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.sm)
                                    fontSize(10.px)
                                    cursor("pointer")
                                }
                            }) { Text("Clear") }
                        }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            property("flex-wrap", "wrap")
                            gap(6.px)
                        }
                    }) {
                        allTags.forEach { tag ->
                            val isSelected = tag.id in selectedTagIds
                            Button({
                                onClick {
                                    selectedTagIds = if (isSelected) {
                                        selectedTagIds - tag.id
                                    } else {
                                        selectedTagIds + tag.id
                                    }
                                }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(if (isSelected) Color("#7c3aed") else Color(colors.surface))
                                    color(if (isSelected) Color("#ffffff") else Color(colors.text))
                                    border(1.px, LineStyle.Solid, if (isSelected) Color("#7c3aed") else Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    fontSize(Typography.xs)
                                    cursor("pointer")
                                }
                            }) { Text(tag.name) }
                        }
                    }

                    // Video filter (T061)
                    Div({
                        style {
                            marginTop(Spacing.md)
                            paddingTop(Spacing.md)
                            property("border-top", "1px solid ${colors.border}")
                        }
                    }) {
                        Div({
                            style {
                                fontSize(Typography.sm)
                                fontWeight("600")
                                color(Color(colors.text))
                                marginBottom(Spacing.sm)
                            }
                        }) { Text("Video") }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                gap(6.px)
                            }
                        }) {
                            // All videos option
                            Button({
                                onClick { hasVideo = null }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(if (hasVideo == null) Color("#7c3aed") else Color(colors.surface))
                                    color(if (hasVideo == null) Color("#ffffff") else Color(colors.text))
                                    border(1.px, LineStyle.Solid, if (hasVideo == null) Color("#7c3aed") else Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    fontSize(Typography.xs)
                                    cursor("pointer")
                                }
                            }) { Text("All") }

                            // Video only option
                            Button({
                                onClick { hasVideo = true }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(if (hasVideo == true) Color("#7c3aed") else Color(colors.surface))
                                    color(if (hasVideo == true) Color("#ffffff") else Color(colors.text))
                                    border(1.px, LineStyle.Solid, if (hasVideo == true) Color("#7c3aed") else Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    fontSize(Typography.xs)
                                    cursor("pointer")
                                }
                            }) { Text("Video Only") }

                            // Audio only option
                            Button({
                                onClick { hasVideo = false }
                                style {
                                    padding(6.px, Spacing.md)
                                    backgroundColor(if (hasVideo == false) Color("#7c3aed") else Color(colors.surface))
                                    color(if (hasVideo == false) Color("#ffffff") else Color(colors.text))
                                    border(1.px, LineStyle.Solid, if (hasVideo == false) Color("#7c3aed") else Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    fontSize(Typography.xs)
                                    cursor("pointer")
                                }
                            }) { Text("Audio Only") }
                        }
                    }
                }
            }
        }

        // Content list (T038 - Phase 5 using ContentResponse)
        Div({
            style {
                flex(1)
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            when {
                error != null -> {
                    ErrorDisplay(
                        code = error!!.code,
                        message = error!!.message,
                        details = error!!.details,
                        onRetry = { loadContent(page = 1, append = false) }
                    )
                }
                loading -> {
                    LoadingSpinner()
                }
                content.isEmpty() -> {
                    Div({ style { textAlign("center"); padding(40.px); color(Color(colors.textMuted)) } }) {
                        Text(if (searchQuery.isNotBlank() || selectedTagIds.isNotEmpty()) "No content found with all selected tags" else "No audiobooks available")
                    }
                }
                else -> {
                    content.forEach { contentItem ->
                        ContentCard(contentItem, selectedLanguage) { onSelect(contentItem) }
                    }

                    // Load More button
                    if (hasMore) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.Center)
                                padding(Spacing.lg)
                            }
                        }) {
                            Button({
                                onClick {
                                    if (!loadingMore) {
                                        loadContent(page = currentPage + 1, append = true)
                                    }
                                }
                                style {
                                    padding(Spacing.md, Spacing.xxl)
                                    backgroundColor(if (loadingMore) Color(colors.border) else Color("#7c3aed"))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(BorderRadius.lg)
                                    fontSize(Typography.base)
                                    fontWeight("500")
                                    cursor(if (loadingMore) "not-allowed" else "pointer")
                                    property("pointer-events", if (loadingMore) "none" else "auto")
                                }
                            }) {
                                Text(if (loadingMore) "Loading..." else "Load More")
                            }
                        }
                    }

                    // Show total count
                    Div({
                        style {
                            textAlign("center")
                            padding(Spacing.sm)
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                        }
                    }) {
                        Text("Showing ${content.size} of $totalCount audiobooks")
                    }
                }
            }
        }
    }
}

@Composable
fun AudiobookCard(audiobook: com.castly.shared.dto.AudiobookDto, selectedLanguage: String, onClick: () -> Unit) {
    val colors = AppTheme.colors
    val displayTitle = if (selectedLanguage == "en") audiobook.titleEnglish ?: audiobook.title else audiobook.title
    val displayAuthor = audiobook.authorName
    val imageUrl = audiobook.coverImageUrl

    Div({
        onClick { onClick() }
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(12.px)
            padding(Spacing.md)
            marginBottom(Spacing.md)
            display(DisplayStyle.Flex)
            gap(Spacing.md)
            cursor("pointer")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
            property("content-visibility", "auto")
            property("contain-intrinsic-size", "auto 76px")
        }
    }) {
        // Audiobook cover (with fallback)
        if (imageUrl != null) {
            Img(imageUrl) {
                attr("loading", "lazy")
                attr("decoding", "async")
                style {
                    width(60.px)
                    height(60.px)
                    borderRadius(BorderRadius.lg)
                    property("object-fit", "cover")
                    flexShrink(0)
                }
            }
        } else {
            Div({
                style {
                    width(60.px)
                    height(60.px)
                    backgroundColor(Color("#7c3aed"))
                    borderRadius(BorderRadius.lg)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(Color("#ffffff"))
                    fontSize(Typography.xxl)
                    fontWeight("bold")
                    flexShrink(0)
                }
            }) {
                Text(displayTitle.take(1).uppercase())
            }
        }
        Div({ style { flex(1); overflow("hidden") } }) {
            Div({
                style {
                    fontWeight("600")
                    fontSize(Typography.base)
                    color(Color(colors.text))
                    marginBottom(Spacing.xs)
                    property("white-space", "nowrap")
                    property("text-overflow", "ellipsis")
                    overflow("hidden")
                }
            }) { Text(displayTitle) }
            Div({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                }
            }) { Text(displayAuthor ?: "Unknown Author") }
        }
    }
}

@Composable
fun AudiobookDetailScreen(audiobook: com.castly.shared.dto.AudiobookDto, selectedLanguage: String, onViewEpisodes: () -> Unit) {
    val colors = AppTheme.colors
    val displayTitle = if (selectedLanguage == "en") audiobook.titleEnglish ?: audiobook.title else audiobook.title
    val displayAuthor = audiobook.authorName
    val displayReader = audiobook.readerName
    val displayDescription = audiobook.description
    val imageUrl = audiobook.coverImageUrl

    Div({ style { padding(Spacing.lg) } }) {
        // Audiobook header
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.xxl)
            }
        }) {
            // Large artwork (with fallback)
            if (imageUrl != null) {
                Img(imageUrl) {
                    style {
                        width(150.px)
                        height(150.px)
                        borderRadius(16.px)
                        property("object-fit", "cover")
                        marginBottom(Spacing.lg)
                        property("box-shadow", "0 10px 30px rgba(0, 0, 0, 0.2)")
                    }
                }
            } else {
                Div({
                    style {
                        width(150.px)
                        height(150.px)
                        backgroundColor(Color("#7c3aed"))
                        borderRadius(16.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        color(Color("#ffffff"))
                        fontSize(48.px)
                        fontWeight("bold")
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Text(displayTitle.take(1).uppercase())
                }
            }

            // Title
            Div({
                style {
                    fontSize(Typography.xl)
                    fontWeight("600")
                    color(Color(colors.text))
                    textAlign("center")
                    marginBottom(Spacing.sm)
                }
            }) { Text(displayTitle) }

            // Author and Reader
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.textMuted))
                    textAlign("center")
                    marginBottom(Spacing.md)
                }
            }) {
                Text(displayAuthor ?: "Unknown Author")
                if (displayReader != null) {
                    Br()
                    Text("Read by $displayReader")
                }
            }

            // Audiobook badge
            Div({
                style {
                    display(DisplayStyle.LegacyInlineFlex)
                    padding(Spacing.xs, Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                    color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                    borderRadius(12.px)
                    fontSize(Typography.sm)
                    fontWeight("500")
                    marginBottom(Spacing.md)
                }
            }) { Text("Audiobook") }

            // Tags
            audiobook.tags?.let { tags ->
                if (tags.isNotEmpty()) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            property("flex-wrap", "wrap")
                            property("gap", "6px")
                            justifyContent(JustifyContent.Center)
                        }
                    }) {
                        tags.take(3).forEach { tag ->
                            Div({
                                style {
                                    padding(Spacing.xs, 10.px)
                                    backgroundColor(Color(colors.surfaceHover))
                                    color(Color(colors.text))
                                    borderRadius(BorderRadius.lg)
                                    fontSize(Typography.xs)
                                }
                            }) { Text(tag) }
                        }
                    }
                }
            }
        }

        // Description
        displayDescription?.let { desc ->
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.text))
                    lineHeight("1.5")
                    marginBottom(Spacing.xxl)
                }
            }) { Text(stripHtmlTags(desc)) }
        }

        // View Episodes button
        Button({
            onClick { onViewEpisodes() }
            style {
                width(100.percent)
                padding(14.px)
                backgroundColor(Color("#7c3aed"))
                color(Color("#ffffff"))
                border(0.px)
                borderRadius(12.px)
                fontSize(Typography.lg)
                fontWeight("600")
                cursor("pointer")
            }
        }) { Text("View Episodes") }
    }
}

@Composable
fun AudiobookEpisodesScreen(
    apiClient: ApiClient,
    content: com.castly.admin.dto.ContentResponse,
    selectedLanguage: String,
    onPlay: (com.castly.admin.dto.EpisodeResponse) -> Unit
) {
    val colors = AppTheme.colors
    var episodes by remember { mutableStateOf<List<com.castly.admin.dto.EpisodeResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<ApiClient.ApiResult.Error?>(null) }
    var sortBy by remember { mutableStateOf<String?>(null) }
    var sortDirection by remember { mutableStateOf("ASC") }
    val scope = rememberCoroutineScope()

    LaunchedEffect(content.id, selectedLanguage, sortBy, sortDirection) {
        scope.launch {
            loading = true
            error = null

            when (val result = apiClient.getContentEpisodes(
                contentId = content.id,
                language = selectedLanguage,
                sortBy = sortBy,
                sortDirection = sortBy?.let { sortDirection }
            )) {
                is ApiClient.ApiResult.Success -> {
                    episodes = result.data
                    error = null
                }
                is ApiClient.ApiResult.Error -> {
                    error = result
                }
            }
            loading = false
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            height(100.percent)
        }
    }) {
        EpisodeSortSelector(sortBy, sortDirection, { sortBy = it }, { sortDirection = it })

        // Episodes list
        Div({
            style {
                flex(1)
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            when {
                error != null -> {
                    ErrorDisplay(
                        code = error!!.code,
                        message = error!!.message,
                        details = error!!.details,
                        onRetry = {
                            scope.launch {
                                loading = true
                                error = null
                                when (val result = apiClient.getContentEpisodes(content.id, selectedLanguage, sortBy = sortBy, sortDirection = sortBy?.let { sortDirection })) {
                                    is ApiClient.ApiResult.Success -> { episodes = result.data; error = null }
                                    is ApiClient.ApiResult.Error -> { error = result }
                                }
                                loading = false
                            }
                        }
                    )
                }
                loading -> {
                    LoadingSpinner()
                }
                episodes.isEmpty() -> {
                    Div({ style { textAlign("center"); padding(40.px); color(Color(colors.textMuted)) } }) {
                        Text("No episodes available")
                    }
                }
                else -> {
                    episodes.forEach { episode ->
                        EpisodeCard(episode, content, selectedLanguage) { onPlay(episode) }
                    }
                }
            }
        }
    }
}

@Composable
fun AudiobookEpisodeCard(episode: com.castly.shared.dto.AudiobookEpisodeDto, selectedLanguage: String, onPlay: () -> Unit) {
    val colors = AppTheme.colors
    val displayTitle = episode.title

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(12.px)
            padding(Spacing.md)
            marginBottom(Spacing.md)
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.md)
                marginBottom(Spacing.sm)
            }
        }) {
            Div({ style { flex(1) } }) {
                Div({
                    style {
                        fontWeight("600")
                        fontSize(Typography.base)
                        color(Color(colors.text))
                        marginBottom(Spacing.xs)
                    }
                }) {
                    episode.episodeNumber?.let {
                        Text("Ep. $it: ")
                    }
                    Text(displayTitle)
                }
                Div({
                    style {
                        fontSize(Typography.sm)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text(episode.duration?.let { formatDurationPreview(it) } ?: "--")
                }
            }
        }

        // Play button
        Button({
            onClick { onPlay() }
            style {
                width(100.percent)
                padding(10.px)
                backgroundColor(Color("#7c3aed"))
                color(Color("#ffffff"))
                border(0.px)
                borderRadius(BorderRadius.lg)
                fontSize(Typography.base)
                fontWeight("500")
                cursor("pointer")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(Spacing.sm)
            }
        }) {
            Text("Play")
        }
    }
}

@Composable
fun AudiobookPlayerScreen(
    content: com.castly.admin.dto.ContentResponse,
    episode: com.castly.admin.dto.EpisodeResponse,
    selectedLanguage: String,
    isPlaying: Boolean,
    onPlayPause: () -> Unit
) {
    val colors = AppTheme.colors
    val displayContentTitle = content.title
    val displayEpisodeTitle = episode.title
    val imageUrl = episode.imageUrl ?: content.coverImageUrl

    Div({
        style {
            padding(Spacing.xxl)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            height(100.percent)
            property("box-sizing", "border-box")
        }
    }) {
        // Large artwork (with fallback)
        if (imageUrl != null) {
            Img(imageUrl) {
                style {
                    width(200.px)
                    height(200.px)
                    borderRadius(16.px)
                    property("object-fit", "cover")
                    marginBottom(Spacing.xxl)
                    property("box-shadow", "0 10px 40px rgba(0, 0, 0, 0.3)")
                }
            }
        } else {
            Div({
                style {
                    width(200.px)
                    height(200.px)
                    backgroundColor(Color("#7c3aed"))
                    borderRadius(16.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(Color("#ffffff"))
                    fontSize(64.px)
                    fontWeight("bold")
                    marginBottom(Spacing.xxl)
                    property("box-shadow", "0 10px 40px rgba(124, 58, 237, 0.3)")
                }
            }) {
                Text(displayContentTitle.take(1).uppercase())
            }
        }

        // Episode info
        Div({
            style {
                textAlign("center")
                marginBottom(32.px)
                width(100.percent)
            }
        }) {
            Div({
                style {
                    fontWeight("600")
                    fontSize(Typography.xl)
                    color(Color(colors.text))
                    marginBottom(Spacing.sm)
                }
            }) { Text(displayEpisodeTitle) }
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.xs)
                }
            }) { Text(displayContentTitle) }

            // Duration and Creator
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                    justifyContent(JustifyContent.Center)
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                    marginTop(Spacing.sm)
                }
            }) {
                Span { Text(episode.audioDurationSeconds?.let { formatDurationPreview(it) } ?: "--") }
                if (content.creators.isNotEmpty()) {
                    Span { Text("•") }
                }

                if (content.creators.isNotEmpty()) {
                    Span { Text(content.creators.first().name) }
                }
            }

            // Video information display (T059)
            episode.video?.let { videoObj ->
                Div({
                    style {
                        marginTop(Spacing.lg)
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.lg)
                        fontSize(Typography.sm)
                    }
                }) {
                    Div({
                        style {
                            fontWeight("600")
                            color(Color(colors.text))
                            marginBottom(Spacing.sm)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                        }
                    }) {
                        Text("🎥 Video Available")
                    }
                    Div({
                        style {
                            color(Color(colors.textMuted))
                            fontSize(Typography.xs)
                        }
                    }) {
                        Div {
                            B { Text("ID: ") }
                            Text(videoObj.id)
                        }
                        videoObj.provider?.let { provider ->
                            Div {
                                B { Text("Provider: ") }
                                Text(provider)
                            }
                        }
                        Div {
                            B { Text("URL: ") }
                            Text(videoObj.url.take(50) + if (videoObj.url.length > 50) "..." else "")
                        }
                    }
                }
            }
        }

        // Playback controls
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.xxl)
            }
        }) {
            // Rewind
            Button({
                style {
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    backgroundColor(Color(colors.surfaceHover))
                    border(0.px)
                    cursor("pointer")
                    fontSize(Typography.lg)
                }
            }) { Text("-15") }

            // Play/Pause
            Button({
                onClick { onPlayPause() }
                style {
                    width(72.px)
                    height(72.px)
                    borderRadius(50.percent)
                    backgroundColor(Color("#7c3aed"))
                    color(Color("#ffffff"))
                    border(0.px)
                    cursor("pointer")
                    fontSize(Typography.xxl)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) { Text(if (isPlaying) "||" else ">") }

            // Forward
            Button({
                style {
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    backgroundColor(Color(colors.surfaceHover))
                    border(0.px)
                    cursor("pointer")
                    fontSize(Typography.lg)
                }
            }) { Text("+30") }
        }
    }
}

@Composable
fun AudiobookMiniPlayer(
    episode: com.castly.shared.dto.AudiobookEpisodeDto,
    selectedLanguage: String,
    isPlaying: Boolean,
    onPlayPause: () -> Unit,
    onClick: () -> Unit
) {
    val colors = AppTheme.colors
    val displayTitle = episode.title
    Div({
        onClick { onClick() }
        style {
            padding(Spacing.md, Spacing.lg)
            backgroundColor(Color("#7c3aed"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.md)
            cursor("pointer")
        }
    }) {
        // Mini artwork
        Div({
            style {
                width(40.px)
                height(40.px)
                backgroundColor(Color("#6d28d9"))
                borderRadius(BorderRadius.md)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                color(Color("#ffffff"))
                fontSize(Typography.lg)
                fontWeight("bold")
                flexShrink(0)
            }
        }) { Text(displayTitle.take(1).uppercase()) }

        // Title
        Div({
            style {
                flex(1)
                color(Color("#ffffff"))
                fontSize(Typography.base)
                fontWeight("500")
                property("white-space", "nowrap")
                property("text-overflow", "ellipsis")
                overflow("hidden")
            }
        }) { Text(displayTitle) }

        // Play/Pause button
        Button({
            onClick {
                it.stopPropagation()
                onPlayPause()
            }
            style {
                width(36.px)
                height(36.px)
                borderRadius(50.percent)
                backgroundColor(Color("#ffffff"))
                color(Color("#7c3aed"))
                border(0.px)
                cursor("pointer")
                fontSize(Typography.base)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexShrink(0)
            }
        }) { Text(if (isPlaying) "||" else ">") }
    }
}
